import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import RouterControl from './RouterControl';
import history from './history';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@progress/kendo-theme-default/dist/all.css';
import './css/fsa-design-system.css';
import './fsa-style/fsa-style.scss';
import 'react-notifications/lib/notifications.css';
import { UserProvider } from './components/UserContext/UserContext';
import NotificationProvider from './components/Notification/NotificationProvider';
import { Suspense } from 'react';
import { GlobalLoader } from './newcomponents/GlobalLoader/GlobalLoader';
import withClearCache from './ClearCache';
import { configureAxiosInterceptors } from './utils/helper/axiosInterceptor';
import InfoPopupModal from './newcomponents/InfoPopupModal/InfoPopupModal';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { jwtDecode } from 'jwt-decode';
import { getSystemConfigDetails, getRedactionConfiguration } from './services/inBasketService';
import { searchPageColumnsAPI } from './pages/Search/SearchServices';
import { fetchBuowInfo } from './components/Login/LoginService';
import { v4 as uuidv4 } from 'uuid';
const ClearCacheComponent = withClearCache(MainApp);

function App() {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    configureAxiosInterceptors(setIsLoading);
  }, []);
  return (
    <>
      {isLoading && <GlobalLoader />}
      <ClearCacheComponent />
    </>
  );
}

function MainApp(props) {
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [loginMessage, setLoginMessage] = useState('');

  useEffect(() => {
    let debounceId = null;

    const applyKendoA11yStyles = () => {
      document.querySelectorAll('.k-grid-norecords:not([data-a11y-applied])').forEach((noRecordsElement) => {
        noRecordsElement.setAttribute('aria-label', 'No records available');
        noRecordsElement.style.color = '#333';
        noRecordsElement.style.backgroundColor = '#fff';
        noRecordsElement.style.opacity = '1';
        noRecordsElement.style.fontSize = '16px';
        noRecordsElement.setAttribute('data-a11y-applied', '1');
      });

      document.querySelectorAll('.k-picker.k-dropdown-list.k-dropdown:not([data-a11y-applied])').forEach((selectElement) => {
        if (!selectElement.hasAttribute('aria-label')) {
          selectElement.setAttribute('aria-label', 'Page number selection');
        }
        selectElement.style.color = '#333';
        selectElement.style.backgroundColor = '#fff';
        selectElement.style.opacity = '1';
        selectElement.style.width = 'auto';
        selectElement.style.border = '1px solid #ccc';
        selectElement.setAttribute('data-a11y-applied', '1');
      });

      document.querySelectorAll('.k-pager-sizes select:not([data-a11y-applied])').forEach((pageSizeDropdown) => {
        if (!pageSizeDropdown.hasAttribute('aria-label')) {
          pageSizeDropdown.setAttribute('aria-label', 'Select page size');
        }
        pageSizeDropdown.style.color = '#333';
        pageSizeDropdown.style.backgroundColor = '#fff';
        pageSizeDropdown.style.opacity = '1';
        pageSizeDropdown.style.width = 'auto';
        pageSizeDropdown.style.border = '1px solid #ccc';
        pageSizeDropdown.setAttribute('data-a11y-applied', '1');
      });
    };

    const observer = new MutationObserver((mutations) => {
      let hasAddedElement = false;
      for (const m of mutations) {
        if (m.addedNodes.length === 0) continue;
        for (const node of m.addedNodes) {
          if (node.nodeType === 1) {
            hasAddedElement = true;
            break;
          }
        }
        if (hasAddedElement) break;
      }
      if (!hasAddedElement) return;

      if (debounceId) clearTimeout(debounceId);
      debounceId = setTimeout(applyKendoA11yStyles, 100);
    });

    applyKendoA11yStyles();

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      if (debounceId) clearTimeout(debounceId);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    // Function to check sessionStorage values
    const checkSessionStorage = () => {
      const showPopup = sessionStorage.getItem('showInfoPopup') === 'true';
      const message = JSON.parse(window.localStorage.getItem('system_config'))?.loginMessage;

      // Update states as required
      if (showPopup && message) {
        setShowInfoPopup(true);
        setLoginMessage(message);

        // Clear 'showInfoPopup' in sessionStorage to prevent multiple triggers
        sessionStorage.removeItem('showInfoPopup');
      }
    };

    // Run the function on mount
    checkSessionStorage();

    // Add a listener for 'storage' events (for multi-tab support)
    const handleStorageChange = (event) => {
      if (event.key === 'showInfoPopup' || event.key === 'system_config') {
        checkSessionStorage();
      }
    };

    // Add a listener for the custom event
    const handleLoginEvent = (event) => {
      const { showInfoPopup, loginMessage } = event.detail;
      if (showInfoPopup && loginMessage) {
        setShowInfoPopup(true);
        setLoginMessage(loginMessage);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('loginSuccess', handleLoginEvent);

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('loginSuccess', handleLoginEvent);
    };
  }, []);
  const securityApi = {
    createAuthorizationToken: (accessToken, username) =>
      fetch(`${window.env.REACT_APP_BACKEND_URL}/security_api/auth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          username: username,
          Authorization: `Bearer ${accessToken}`,
        },
      }),
  };
  const userInfo = {
    fetchUserInfo: (token) =>
      fetch(`${window.env.REACT_APP_BACKEND_URL}/security_api/security/userinfo`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
  };
  // Function to handle closing the modal
  const handleClosePopup = () => {
    setShowInfoPopup(false);
    sessionStorage.setItem('showInfoPopup', 'false');
    setLoginMessage('');
  };
  const fetchSystemFolderData = async (payloadData) => {
    await searchPageColumnsAPI(payloadData).then(async (response) => {
      sessionStorage.setItem('SystemColumns', JSON.stringify(response?.data));
      let columnNewArray = [
        {
          metaId: 0,
          field: 'SELECTED_FIELD',
          title: 'Select',
        },
      ];
      await response?.data?.map((column, idx) => {
        columnNewArray.push({
          metaId: '_id',
          field: 'key.object_specific.' + column.name.replace(' ', ''),
          title: column.name,
          dataType: 'text',
          showMenuIcon: 'true',
          sortType: '',
          data: column,
        });
        if (response.data.length === idx + 1) {
          columnNewArray.push({
            metaId: idx + 1,
            field: 'VIEW_DETAILS',
            title: '',
          });
        }
        sessionStorage.setItem('systemFolderOptions', JSON.stringify(columnNewArray));
      });
    });
  };
  const onSigninCallback = async (user) => {
    try {
      window.history.replaceState({}, document.title, window.location.pathname);

      const response = await securityApi.createAuthorizationToken(user.access_token, user?.profile?.preferred_username || user?.profile?.name || '');

      sessionStorage.setItem('user', JSON.stringify(user));
      if (!response.ok) {
        throw new Error('Authorization token creation failed');
      }

      const authJwt = await response.text();

      if (!authJwt) {
        throw new Error('No auth token received');
      }
      sessionStorage.setItem('authorizationToken', authJwt);

      const payload = JSON.parse(atob(authJwt.split('.')[1]));
      sessionStorage.setItem('permissions', JSON.stringify(payload['https://brillient.net/permissions'] || []));
      const sessionClaimKey = window.env.REACT_APP_SESSION_ID;
      const sessionId = sessionClaimKey && Object.prototype.hasOwnProperty.call(response, sessionClaimKey) ? response[sessionClaimKey] : uuidv4();
      sessionStorage.setItem('sessionId', sessionId);
      sessionStorage.setItem('groups', JSON.stringify(payload['groups'] || []));
      sessionStorage.setItem('userId', JSON.stringify(payload['userId'] || []));

      await new Promise((resolve) => setTimeout(resolve, 50));
      await getRedactionConfiguration(window.env.REACT_APP_CLIENT_CONTEXT_ID);

      await getSystemConfigDetails(window.env.REACT_APP_CLIENT_CONTEXT_ID);

      await fetchSystemFolderData();
      const redirectTo = sessionStorage.getItem('redirectTo');
      const hasBuowLink = redirectTo ? true : false;

      if (hasBuowLink === true) {
        sessionStorage.setItem('showInfoPopup', 'false');
        const buowId = redirectTo?.split('buow/')[1]?.split('?')[0]?.trim();
        let data = {
          token: sessionStorage.getItem('authorizationToken'),
          buow: buowId,
        };
        try {
          const result = await fetchBuowInfo(data);
          if (result?.data) {
            const res = result?.data[0];

            const redirectToObj = {
              path: redirectTo,
              id: res?.id,
              buowName: res?.buowName,
              buowType: res?.buowType,
              buowId: res?.buowId,
            };
            sessionStorage.setItem('BUOW_id', res?.buowId);
            sessionStorage.setItem('redirectTo', JSON.stringify(redirectToObj));
            sessionStorage.setItem('lock_id', res?.id);
            localStorage.setItem('buow_name', res?.buowName);
            localStorage.setItem('buow_type', res?.buowType);
          }
        } catch (error) {
          console.log('Error Occurred in Fetching Buow Details in login page', error);
        }
      } else {
        sessionStorage.setItem('showInfoPopup', 'true');
        const loginEvent = new CustomEvent('loginSuccess', {
          detail: {
            showInfoPopup: true,
            loginMessage: JSON.parse(window.localStorage.getItem('system_config'))?.loginMessage,
          },
        });
        window.dispatchEvent(loginEvent);
      }
      window.location.href = '/inbasketpage';
    } catch (e) {
      console.error('Login failed', e);
      window.location.replace('/login?error=auth_failed');
    }
  };
  const oidcConfig = {
    authority: window.env.REACT_APP_IDP_SPA_URL,
    client_id: window.env.REACT_APP_IDP_SPA_CLIENT_ID,
    redirect_uri: window.env.REACT_APP_IDP_SPA_REDIRECT_URL,
    silent_redirect_uri: window.env.REACT_APP_IDP_SPA_SILENT_REDIRECT_URL,
    scope: window.env.REACT_APP_IDP_SPA_OAUTH_SCOPE,
    response_type: 'code',
    loadUserInfo: false,
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 60,
    onSigninCallback: onSigninCallback,
    post_logout_redirect_uri: window.env.REACT_APP_IDP_SPA_REDIRECT_URL,
  };
  return (
    <AuthProvider {...oidcConfig}>
      <SilentRenewSync />
      <UserProvider>
        <NotificationProvider>
          <div className="App">
            <Suspense
              fallback={
                <div>
                  <GlobalLoader />
                </div>
              }
            >
              <Router history={history}>
                <RouterControl />
              </Router>
            </Suspense>
            {showInfoPopup && loginMessage && <InfoPopupModal show={showInfoPopup} onClose={handleClosePopup} message={loginMessage} />}
          </div>
        </NotificationProvider>
      </UserProvider>
    </AuthProvider>
  );
}

function SilentRenewSync() {
  const auth = useAuth();
  useEffect(() => {
    if (!auth?.events) return undefined;
    const removeUserLoaded = auth.events.addUserLoaded((user) => {
      sessionStorage.setItem('user', JSON.stringify(user));
    });
    const removeSilentRenewError = auth.events.addSilentRenewError((e) => {
      console.error('Silent renew failed', e);
      sessionStorage.clear();
      localStorage.clear();
      window.location.replace('/login?error=session_expired');
    });
    return () => {
      removeUserLoaded();
      removeSilentRenewError();
    };
  }, [auth?.events]);
  return null;
}

export default App;
